// Прямые вызовы VK API из браузера пользователя
const VK_API = 'https://api.vk.com/method';
const API_VERSION = '5.199';
const RATE_DELAY = 340; // ~3 req/s — безопасный темп

let _token = null;

export function setVkToken(token) {
  _token = token;
  localStorage.setItem('vk_client_token', token);
}

export function getVkToken() {
  if (!_token) _token = localStorage.getItem('vk_client_token') || '';
  return _token;
}

function sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

async function call(method, params = {}) {
  const token = getVkToken();
  if (!token) throw new Error('VK token not set');
  
  const url = new URL(`${VK_API}/${method}`);
  url.searchParams.set('access_token', token);
  url.searchParams.set('v', API_VERSION);
  url.searchParams.set('lang', 'ru');
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  });
  
  const res = await fetch(url.toString());
  const data = await res.json();
  
  if (data.error) {
    const err = new Error(data.error.error_msg);
    err.code = data.error.error_code;
    throw err;
  }
  return data.response;
}

// Универсальный пагинатор
async function paginate(method, params, { itemsKey = 'items', onProgress } = {}) {
  const results = [];
  let offset = 0;
  const count = 1000;
  let total = null;
  
  while (true) {
    const resp = await call(method, { ...params, count, offset });
    const items = Array.isArray(resp) ? resp : (resp[itemsKey] || []);
    if (total === null) total = resp.count || items.length;
    
    results.push(...items);
    if (onProgress) onProgress(results.length, total);
    
    if (items.length < count) break;
    offset += count;
    await sleep(RATE_DELAY);
  }
  return results;
}

// Резолв group_id из ссылки/screen_name
export async function resolveGroupId(groupIdStr) {
  const cleaned = groupIdStr.replace('https://vk.com/', '').replace('vk.com/', '').trim();
  if (/^-?\d+$/.test(cleaned)) return Math.abs(parseInt(cleaned));
  const resp = await call('groups.getById', { group_id: cleaned, fields: 'id' });
  const group = Array.isArray(resp) ? resp[0] : resp.groups?.[0];
  return group?.id;
}

// Все подписчики группы
export async function fetchGroupMembers(groupId, { fields = 'first_name,last_name,photo_50,sex,city,bdate', onProgress } = {}) {
  const items = await paginate('groups.getMembers', { group_id: groupId, fields }, { onProgress });
  return items;
}

// Лайкнувшие пост
export async function fetchPostLikers(ownerId, postId, { onProgress } = {}) {
  return paginate('likes.getList', { type: 'post', owner_id: ownerId, item_id: postId, extended: 1, friends_only: 0 }, { itemsKey: 'items', onProgress });
}

// Комментарии к посту
export async function fetchPostComments(ownerId, postId, { onProgress } = {}) {
  return paginate('wall.getComments', { owner_id: ownerId, post_id: postId, need_likes: 1, thread_items_count: 0 }, { onProgress });
}

// Посты со стены
export async function fetchWallPosts(ownerId, { count = 100, onProgress } = {}) {
  const items = [];
  let offset = 0;
  let total = null;
  while (items.length < count) {
    const resp = await call('wall.get', { owner_id: ownerId, count: Math.min(100, count - items.length), offset, extended: 0 });
    if (total === null) total = Math.min(resp.count, count);
    items.push(...resp.items);
    if (onProgress) onProgress(items.length, total);
    if (resp.items.length < 100 || items.length >= count) break;
    offset += 100;
    await sleep(RATE_DELAY);
  }
  return items;
}

// Поиск групп
export async function searchGroups(query, { count = 100, type = 'group', sort = 6 } = {}) {
  return paginate('groups.search', { q: query, type, sort, count: Math.min(count, 1000) }, { itemsKey: 'items' });
}

// Участники опроса
export async function fetchPollVoters(ownerId, pollId, answerIds) {
  const results = [];
  for (const answerId of answerIds) {
    const items = await paginate('polls.getVoters', { owner_id: ownerId, poll_id: pollId, answer_ids: answerId, fields: 'first_name,last_name' }, { itemsKey: 'users' });
    results.push(...items);
    await sleep(RATE_DELAY);
  }
  return results;
}

// Профили пользователей батчами
export async function fetchProfiles(userIds, { fields = 'first_name,last_name,photo_50,sex,city,bdate,followers_count' } = {}) {
  const results = [];
  for (let i = 0; i < userIds.length; i += 500) {
    const batch = userIds.slice(i, i + 500);
    const profiles = await call('users.get', { user_ids: batch.join(','), fields });
    results.push(...(Array.isArray(profiles) ? profiles : []));
    if (i + 500 < userIds.length) await sleep(RATE_DELAY);
  }
  return results;
}


// Strip VK URL to bare group identifier.
// "https://vk.com/durov" → "durov", "@club1" → "club1", "durov" → "durov"
export function extractVkGroupId(input) {
  const s = (input || '').trim();
  if (!s) return s;
  if (s.startsWith('@')) return s.slice(1);
  const m = s.match(/(?:https?:\/\/)?(?:www\.)?vk\.com\/([a-zA-Z0-9_.\-]+)/);
  if (m) return m[1];
  return s;
}

export default { call, paginate, resolveGroupId, fetchGroupMembers, fetchPostLikers, fetchPostComments, fetchWallPosts, searchGroups, fetchPollVoters, fetchProfiles, setVkToken, getVkToken };
